import React, { useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { useScrollViewOn } from "Helpers/useScrollView";
import { BrandContext } from "./BrandContext.js";
import Section from "./Section.js";
import { VenueBrandForContext } from "./venueBrand.js";
import type { Menu } from "./types.js";

const DefaultEmptyMenu = () => {
  const { t } = useTranslation();
  return <div className="text-center">{t("publicMenus.emptyMenu")}</div>;
};

interface MenuComponents {
  SectionFooter: React.FC;
  SectionBlurb: React.FC;
  SectionHeader: React.FC;
}
interface MenuProps {
  menu: Menu;
  EmptyMenu: React.FC;
  MenuComponents: MenuComponents;
  scrollContainterId: string;
}
const Menu = ({
  menu,
  EmptyMenu,
  MenuComponents,
  scrollContainterId,
}: MenuProps) => {
  const { t } = useTranslation();

  const { isPlayful, isElegant, isSimple, isBrutalist, ...venueBrand } =
    useContext(BrandContext) as VenueBrandForContext;
  const contentRefs = useRef(new Map());
  const headerRef = useRef<HTMLDivElement>(null);
  const headerTitlesRef = useRef(new Map());

  const getHeaderTitlesMap = () => {
    if (!headerTitlesRef.current) {
      // Initialize the Map on first usage.
      headerTitlesRef.current = new Map();
    }
    return headerTitlesRef.current;
  };
  const getMap = () => {
    if (!contentRefs.current) {
      // Initialize the Map on first usage.
      contentRefs.current = new Map();
    }
    return contentRefs.current;
  };

  const visibleSection = useScrollViewOn(scrollContainterId)({
    contentRefs,
    headerRef,
  });

  React.useEffect(() => {
    const currentHeader = getHeaderTitlesMap().get(visibleSection);
    if (visibleSection && currentHeader) {
      currentHeader.ele.scrollIntoView({ inline: "start" });
    }
  }, [visibleSection]);

  const menuSections = menu.sections.filter(
    (section) => section.items.length > 0
  );

  return (
    <div className="pb-24 space-y-24">
      <div
        id="sectionsList"
        ref={headerRef}
        className="sticky top-0 flex text-center bg-white overflow-x-auto snap-x z-10 -mx-12"
      >
        {menu.sections.length > 1 &&
          menuSections.map((section, index) => (
            <a
              id={`title-${section.name}`}
              href={`#${section.name}`}
              key={section.uuid}
              ref={(ele) => {
                const map = getHeaderTitlesMap();
                if (ele) {
                  map.set(section.uuid, { content: section, ele });
                } else {
                  map.delete(section.uuid);
                }
              }}
              className={classNames(
                "snap-start flex-1 whitespace-nowrap no-underline text-primary-1000 px-16 pb-8 pt-12 ",

                {
                  "text-venue-brand": isPlayful,
                  "uppercase font-light text-xs": isElegant,
                  "border-neutral-200 border-b-4":
                    section.uuid != visibleSection,
                  "border-neutral-400 border-b-4 border-venue-brand":
                    section.uuid === visibleSection ||
                    (visibleSection == null && index == 0),
                }
              )}
            >
              {section.name}
            </a>
          ))}
      </div>
      <div id="PagesParent" className="space-y-24 px-12">
        {menuSections.length == 0 &&
          (EmptyMenu ? <EmptyMenu /> : <DefaultEmptyMenu />)}

        {menuSections.map((section) => (
          <div
            id={section.name}
            key={section.uuid}
            className=""
            style={{
              scrollMarginTop: `${
                headerRef.current &&
                headerRef.current.getBoundingClientRect().height
              }px`,
            }}
            ref={(ele) => {
              const map = getMap();
              if (ele) {
                map.set(section.uuid, { content: section, ele });
              } else {
                map.delete(section.uuid);
              }
            }}
          >
            <Section
              section={section}
              SectionBlurb={MenuComponents?.SectionBlurb}
              SectionFooter={MenuComponents?.SectionFooter}
              SectionHeader={MenuComponents?.SectionHeader}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Menu;
